import type { TFunction } from 'i18next';

export interface DragHintInput {
  selectionCount: number;
  selectionBlocked: boolean;
  selectionSpansParents: boolean;
  lockedCount: number;
  allLocked: boolean;
  canReparent: boolean;
  siblingsOnly: boolean;
  source: {
    typeName: string | undefined;
    levelOrder: number | undefined;
    isUnparented: boolean;
  };
  hoveredParentLevel?: number;
  levelNameByLevel: ReadonlyMap<number, string> | null;
  levelsWithTypes: ReadonlySet<number>;
}

export const computeDragHint = (t: TFunction, input: DragHintInput): string | null => {
  const {
    selectionCount,
    selectionBlocked,
    selectionSpansParents,
    lockedCount,
    allLocked,
    canReparent,
    siblingsOnly,
    source,
    hoveredParentLevel,
    levelNameByLevel,
    levelsWithTypes,
  } = input;

  if (selectionBlocked) {
    return t(($) => $.views.table.dragBlockedMixedLevels);
  }

  const sourceLevelName =
    source.levelOrder !== undefined ? levelNameByLevel?.get(source.levelOrder) : undefined;

  if (canReparent && allLocked) {
    if (selectionSpansParents) {
      return t(($) => $.views.table.dragLegend.hintLockedMixedParents);
    }

    if (!sourceLevelName) {
      return t(($) => $.views.table.reparentBlockedManagedByIntegration);
    }

    return t(($) => $.views.table.dragLegend.hintReorderAmongLevelLockedByIntegration, {
      levelName: sourceLevelName,
      count: selectionCount,
    });
  }

  if (canReparent && lockedCount > 0) {
    return t(($) => $.views.table.dragLegend.hintSomeLockedByIntegration, {
      count: lockedCount,
    });
  }

  const sourceLevel = source.levelOrder;
  if (!source.typeName || sourceLevel === undefined) return null;

  if (siblingsOnly) {
    return t(($) => $.views.table.dragLegend.hintSiblingsOnly, {
      itemType: source.typeName,
    });
  }

  // An unparented item can always reorder against any other unparented item, whatever its level.
  const targetLevel = hoveredParentLevel ?? sourceLevel - 1;
  const hasValidParentType = hoveredParentLevel !== undefined || levelsWithTypes.has(targetLevel);

  if (!hasValidParentType) {
    if (source.isUnparented) {
      return t(($) => $.views.table.dragLegend.hintReorderUnparented);
    }
    if (!sourceLevelName) return null;
    return t(($) => $.views.table.dragLegend.hintReorderAmongLevel, {
      levelName: sourceLevelName,
    });
  }

  const targetLevelName = levelNameByLevel?.get(targetLevel);
  if (!targetLevelName) return null;

  if (source.isUnparented) {
    return t(($) => $.views.table.dragLegend.hintReorderUnparentedOrDropInside, {
      levelName: targetLevelName,
    });
  }

  if (!sourceLevelName) {
    return t(($) => $.views.table.dragLegend.hintDropInside, {
      levelName: targetLevelName,
    });
  }
  return t(($) => $.views.table.dragLegend.hintDropInsideOrReorderAmongLevel, {
    targetLevelName,
    sourceLevelName,
  });
};
